import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Typography } from "antd"
import type React from "react"

const { Text } = Typography
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import HeadwordSearchForm, { HEAD_WORD_STATUS } from "./components/HeadwordSearchForm"
import HeadwordTable from "./components/HeadwordTable"
import HeadwordTableActions from "./components/HeadwordTableActions"
import { headwordService, type HeadwordSearchParams, type HeadwordApiParams } from "@/services/dictionary/headwordApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

export const HeadwordPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useTableParams<HeadwordSearchParams>({
    ...PAGE_CONFIG,
    status: HEAD_WORD_STATUS.PUBLISHED,
    search: "",
  })
  const [total, setTotal] = useState<number>(0)

  const {
    data: result,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["headword_list", searchParams],
    queryFn: () => {
      // Map frontend `per_page` to backend `limit`
      const apiParams: HeadwordApiParams = {
        page: searchParams.page,
        limit: searchParams.per_page,
        status: searchParams.status,
        search: searchParams.search || undefined,
      }
      return headwordService.list(apiParams)
    },
  })

  useEffect(() => {
    if (result?.paginate.total) {
      setTotal(result.paginate.total)
    }
  }, [result?.paginate])

  const handleSearch = (values: Omit<HeadwordSearchParams, "page" | "per_page">) => {
    setSearchParams({
      ...values,
      page: 1,
      per_page: searchParams.per_page,
    })
  }

  const handleView = (id: number) => {
    navigate(`/dict/headword/${id}`)
  }

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])

  const handleSelectionChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const tableLoading = isFetching

  return (
    <div className="flex flex-col gap-2">
      <HeadwordSearchForm
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={{
          search: searchParams.search,
          status: searchParams.status,
        }}
      />
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow" }}
        title={
          <div className="flex items-center">
            <h2>Danh sách từ gốc</h2>
            <Divider orientation="vertical" />
            <Text type="secondary" className="text-sm!">{`${selectedRowKeys.length} mục đã được chọn`}</Text>
            <Divider orientation="vertical" />
            <HeadwordTableActions onRefresh={refetch} loading={tableLoading} />
          </div>
        }
      >
        <HeadwordTable
          data={result?.data || []}
          loading={tableLoading}
          onView={handleView}
          selectedRowKeys={selectedRowKeys}
          onSelectionChange={handleSelectionChange}
          pagination={{
            pageSize: searchParams.per_page,
            current: searchParams.page,
            total,
            ...PAGINATION_CONFIG,
            onChange(page: number, pageSize: number) {
              setSearchParams({
                ...searchParams,
                page: page,
                per_page: pageSize,
              })
            },
          }}
        />
      </Card>
    </div>
  )
}

export default HeadwordPage
